import {
  WHATSAPP_API_CHANNEL,
  MESSENGER_PAGE_CHANNEL,
  INSTAGRAM_DM_CHANNEL,
  isWhatsappApiChannel,
  isMessengerPageChannel,
  isInstagramDmChannel,
} from "./connectionChannel";

/** Channels with Meta 24h free-form messaging window (WhatsApp Cloud + Messenger + Instagram). */
export function isMetaServiceWindowChannel(channel) {
  return (
    isWhatsappApiChannel(channel) ||
    isMessengerPageChannel(channel) ||
    isInstagramDmChannel(channel)
  );
}

/** @deprecated prefer isMetaServiceWindowChannel — kept for existing imports. */
export function isCloudTicketChannel(channel) {
  return isMetaServiceWindowChannel(channel);
}

/** WhatsApp Cloud + Messenger Page: no edit/delete via Graph. */
export function isCloudMessageEditDeleteDisabled(channel) {
  return isMetaServiceWindowChannel(channel);
}

/** Resolves Cloud/Baileys channel from ticket list, messages API, or socket shapes. */
export function resolveTicketChannel(ticket) {
  if (!ticket || typeof ticket !== "object") {
    return "webchat";
  }
  if (typeof ticket.channel === "string" && ticket.channel.length > 0) {
    return ticket.channel;
  }
  const nested =
    ticket.whatsapp?.channel ??
    ticket.Whatsapps?.channel ??
    null;
  if (typeof nested === "string" && nested.length > 0) {
    return nested;
  }
  return "webchat";
}

export function extractCloudTicketMeta(ticket) {
  if (!ticket || typeof ticket !== "object") {
    return null;
  }
  return {
    channel: resolveTicketChannel(ticket),
    serviceWindowExpiresAt: ticket.serviceWindowExpiresAt ?? null,
    lastCustomerMessageAt: ticket.lastCustomerMessageAt ?? null,
  };
}

export function isCloudServiceWindowClosed(serviceWindowExpiresAt, now = new Date()) {
  if (!serviceWindowExpiresAt) {
    return true;
  }
  const expires = new Date(serviceWindowExpiresAt);
  if (Number.isNaN(expires.getTime())) {
    return true;
  }
  return now.getTime() >= expires.getTime();
}

/** Whether a Meta text schedule can be sent at sendAt (window still open then). */
export function canScheduleCloudTextAt(
  channel,
  serviceWindowExpiresAt,
  sendAt,
) {
  if (!isMetaServiceWindowChannel(channel)) {
    return true;
  }
  if (!serviceWindowExpiresAt) {
    return false;
  }
  const sendAtDate = sendAt instanceof Date ? sendAt : new Date(sendAt);
  if (Number.isNaN(sendAtDate.getTime())) {
    return false;
  }
  return !isCloudServiceWindowClosed(serviceWindowExpiresAt, sendAtDate);
}

export function getCloudScheduleWindowBlockReason(
  channel,
  serviceWindowExpiresAt,
  sendAt,
) {
  if (!isMetaServiceWindowChannel(channel)) {
    return null;
  }
  if (canScheduleCloudTextAt(channel, serviceWindowExpiresAt, sendAt)) {
    return null;
  }
  if (!serviceWindowExpiresAt) {
    return "no_inbound";
  }
  return "expires_before_send";
}

export function isCloudComposerLocked(channel, serviceWindowExpiresAt, now = new Date()) {
  return (
    isMetaServiceWindowChannel(channel) &&
    isCloudServiceWindowClosed(serviceWindowExpiresAt, now)
  );
}

export function formatServiceWindowExpiry(serviceWindowExpiresAt, locale) {
  if (!serviceWindowExpiresAt) {
    return null;
  }
  const expires = new Date(serviceWindowExpiresAt);
  if (Number.isNaN(expires.getTime())) {
    return null;
  }
  return expires.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** i18n key prefix for service-window copy: ticketComposer.cloudWindow vs messengerWindow. */
export function serviceWindowI18nPrefix(channel) {
  if (isInstagramDmChannel(channel)) {
    return "ticketComposer.instagramWindow";
  }
  return isMessengerPageChannel(channel)
    ? "ticketComposer.messengerWindow"
    : "ticketComposer.cloudWindow";
}

export function scheduleModalWindowI18nPrefix(channel) {
  if (isInstagramDmChannel(channel)) {
    return "scheduleModal.instagramWindow";
  }
  return isMessengerPageChannel(channel)
    ? "scheduleModal.messengerWindow"
    : "scheduleModal.cloudWindow";
}

/** Banner state for Meta messaging composer notices. */
export function getCloudServiceWindowBannerState(
  channel,
  serviceWindowExpiresAt,
  now = new Date(),
) {
  if (!isMetaServiceWindowChannel(channel)) {
    return "hidden";
  }
  if (!serviceWindowExpiresAt) {
    return "no_inbound";
  }
  if (isCloudServiceWindowClosed(serviceWindowExpiresAt, now)) {
    return "closed";
  }
  return "open";
}

export {
  WHATSAPP_API_CHANNEL,
  MESSENGER_PAGE_CHANNEL,
  INSTAGRAM_DM_CHANNEL,
  isWhatsappApiChannel,
  isMessengerPageChannel,
  isInstagramDmChannel,
};
